import { getRegionByCode } from '@luxuryescapes/lib-regions'
import Moment from 'moment'
import qs, { ParsedQs } from 'qs'

import {
  queryKeyAdults,
  queryKeyChildren,
  queryKeyDestinationName,
  queryKeyDestinationId,
  queryKeyLandmarkName,
  queryKeyLandmarkId,
  queryKeyPropertyName,
  queryKeyPropertyId,
  queryKeyBounds,
  queryKeyCheckIn,
  queryKeyCheckOut,
  queryKeyPriceLte,
  queryKeyPriceGte,
  queryKeyPages,
  queryKeyPropertyTypes,
  queryKeyLocations,
  queryKeyHolidayTypes,
  queryKeyAmenities,
  queryKeySelectedOfferIds,
  queryKeyFlexibleNights,
  queryKeyFlexibleMonths,
  queryKeyUserSelectedFlexibleMonths,
  queryKeyInclusions,
  queryKeyMapPinCount,
  queryKeyLat,
  queryKeyLng,
  queryKeyQuery,
  queryKeyTypeaheadId,
  queryKeyExtracting,
  queryKeyTags,
  queryKeySortBy,
} from 'constants/url'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import { pathnameHasRegionCode } from 'lib/string/regionUtils'
import Places from 'constants/places'

// addRegionToPath returns a new path with region
export function addRegionToPath(path: string, regionCode: string) {
  if (pathnameHasRegionCode(path, regionCode)) {
    // already has the region, don't add it again
    return path
  }
  return '/' + regionCode.toLowerCase() + (path === '/' ? '' : path)
}

export function removeRegionFromPath(path: string) {
  const segments = path.split('/').filter(segment => segment)
  if (getRegionByCode(segments[0])) segments.shift()
  return '/' + segments.join('/')
}

// parseSearchString parses location.search provided by react-router into
// an object. `searchString` is in the form of '?orderId=foo&itemId=bar'
export function parseSearchString(search: string | ParsedQs): Record<string, string | undefined> {
  if (!search) {
    return {}
  }

  /**
   * The cast type of `{ [key: string]: string }` is incorrect.
   * The return type of `qs.parse` is not only of string type, but also can be an array or tuples.
   */
  const queryResult = (typeof search === 'string' ? qs.parse(search, { ignoreQueryPrefix: true, duplicates: 'first' }) : search) as Record<string, string | undefined>

  // TODO remove when mobile matches web
  if (queryResult.reservationType === 'instant') {
    queryResult.reservationType = 'instant_booking'
  }

  if (queryResult.reservationType === 'book-later') {
    queryResult.reservationType = 'buy_now_book_later'
  }

  return queryResult
}

export function addQueryParamsToUrl(path: string, params: { [param: string]: any }) {
  const { protocol, host, pathname, search } = new URL(path)
  const combinedQuery = {
    ...parseSearchString(search),
    ...params,
  }
  return `${protocol}//${host}${pathname}?${qs.stringify(combinedQuery)}`
}

export function addQueryParamsToPath(path: string, params: { [param: string]: any }) {
  const [pathWithoutHash, hash] = path.split('#')
  const [pathname, search = ''] = pathWithoutHash.split('?')
  const combinedQuery = {
    ...parseSearchString(search),
    ...params,
  }
  const hashString = hash ? `#${hash}` : ''
  return `${pathname}?${qs.stringify(combinedQuery)}${hashString}`
}

export function getQueryParam(search: string, param: string) {
  return new URLSearchParams(search).get(param) || undefined
}

/**
 * Adds a value to the query param, this assumes multiple items for the same parameter name
 * e.g. if '?fruit=banana' and 'apple' is added, it will result in '?fruit=banana,apple'
 * @param search The current search string
 * @param paramName The query parameters name
 * @param paramValue The query parameters value
 */
export function addSearchParamValue(search: string, paramName: string, paramValue: string | number) {
  const searchParams = new URLSearchParams(search)
  const existingParam = (searchParams.get(paramName) ?? '').split(',').filter(Boolean)
  existingParam.push(paramValue.toString())
  searchParams.set(paramName, existingParam.join(','))

  return searchParams.toString()
}

/**
 * Removes a value from the query parameter, will remove from list of items and delete entire name if
 * no items are left
 * @param search The current search string
 * @param paramName The query parameters name
 * @param paramValue The query parameters value
 */
export function removeSearchParamValue(search: string, paramName: string, paramValue: string | number) {
  const searchParams = new URLSearchParams(search)
  const existingParam = (searchParams.get(paramName) ?? '').split(',').filter(Boolean)
  const newParams = existingParam.filter(value => value !== paramValue.toString())
  if (newParams.length === 0) {
    searchParams.delete(paramName)
  } else {
    searchParams.set(paramName, newParams.join(','))
  }

  return searchParams.toString()
}

/**
 * Swaps whether the parameter given is added or removed on whether it already exists in the search query
 * @param search The current search string
 * @param paramName The query parameters name
 * @param paramValue The query parameters value
 */
export function toggleSearchParamValue(search: string, paramName: string, paramValue: string | number) {
  const searchParams = new URLSearchParams(search)
  const existingParam = (searchParams.get(paramName) ?? '').split(',').filter(Boolean)
  if (existingParam.includes(paramValue.toString())) {
    return removeSearchParamValue(search, paramName, paramValue)
  } else {
    return addSearchParamValue(search, paramName, paramValue)
  }
}

/**
 * Sets (replaces) the search parameter with the value given
 * @param search The current search string
 * @param paramName The query parameters name
 * @param paramValue The query parameters value
 */
export function setSearchParamValue(
  search: string,
  paramName: string,
  paramValue: string | number | Array<string> | Array<number>,
) {
  const searchParams = new URLSearchParams(search)
  if (Array.isArray(paramValue)) {
    searchParams.delete(paramName)
    paramValue.forEach(val => searchParams.append(paramName, val.toString()))
  } else {
    searchParams.set(paramName, paramValue.toString())
  }

  return searchParams.toString()
}

/**
 * Extension of setSearchParamValue that takes an array of param objects
 * @param search The current search string
 * @param params Array of query parameters to set with paramName and paramValue
 * @returns The new search string
 */
export function setManySearchParamValues(
  search: string,
  params: Array<{ paramName: string, paramValue: string | number | Array<string> | Array<number> | undefined }>,
): string {
  const searchParams = new URLSearchParams(search)
  params.forEach(param => {
    if (param.paramValue === undefined) {
      searchParams.delete(param.paramName)
      return
    }

    if (Array.isArray(param.paramValue)) {
      searchParams.delete(param.paramName)
      param.paramValue.forEach(val => searchParams.append(param.paramName, val.toString()))
    } else {
      searchParams.set(param.paramName, param.paramValue.toString())
    }
  })
  return searchParams.toString()
}

/**
 * Deletes all values for a given parameter key from the search string
 * @param search The current search string
 * @param paramsToRemove List of search string keys to remove
 */
export function deleteSearchParams(search: string, ...paramsToRemove: Array<string>) {
  const searchParams = new URLSearchParams(search)
  paramsToRemove.forEach(param => {
    searchParams.delete(param)
  })

  return searchParams.toString()
}

export function validateDestinationSearchParams(params: Record<string, string | undefined>) {
  return !!(
    params[queryKeyAdults] &&
    params[queryKeyChildren] &&
    params[queryKeyDestinationName] &&
    params[queryKeyDestinationId]
  )
}
export function validateLandmarkSearchParams(params: Record<string, string | undefined>) {
  return !!(
    params[queryKeyAdults] &&
    params[queryKeyChildren] &&
    params[queryKeyLandmarkName] &&
    params[queryKeyLandmarkId]
  )
}

export function validatePropertySearchParams(params: Record<string, string | undefined>) {
  return !!(
    params[queryKeyAdults] &&
    params[queryKeyChildren] &&
    params[queryKeyPropertyName] &&
    params[queryKeyPropertyId]
  )
}

export function validateBoundsSearchParams(params: Record<string, string | undefined>) {
  return !!(
    params[queryKeyAdults] &&
    params[queryKeyChildren] &&
    params[queryKeyBounds]
  )
}

export function validateLatLngSearchParams(params: Record<string, string | undefined>) {
  return !!(
    params[queryKeyLat] &&
    params[queryKeyLng]
  )
}

export function validateNaturalLanguageSearchParams(params: Record<string, string | undefined>) {
  return !!(
    params[queryKeyQuery] &&
    params[queryKeyAdults] &&
    params[queryKeyChildren]
  )
}

export function getSearchItemFromURLSearchParams(urlSearchParams: URLSearchParams): App.SearchItem | undefined {
  const destinationName = urlSearchParams.get('destinationName')
  const destinationId = urlSearchParams.get('destinationId')

  if (destinationId && destinationName) {
    // For multiple destination IDs, use the first one for the search item
    // (backward compatibility - searchItem typically represents a single selection)
    const firstDestinationId = destinationId.split(',')[0]
    return {
      searchType: 'destination',
      value: firstDestinationId,
      format: {
        mainText: destinationName,
      },
    }
  }

  const propertyName = urlSearchParams.get('propertyName')
  const propertyId = urlSearchParams.get('propertyId')

  if (propertyId && propertyName) {
    return {
      searchType: 'property',
      value: propertyId,
      format: {
        mainText: propertyName,
      },
    }
  }

  const experienceName = urlSearchParams.get('experienceName')
  const experienceId = urlSearchParams.get('experienceId')

  if (experienceId && experienceName) {
    return {
      searchType: 'experience',
      value: experienceId,
      format: {
        mainText: experienceName,
      },
    }
  }

  const landmarkName = urlSearchParams.get('landmarkName')
  const landmarkId = urlSearchParams.get('landmarkId')

  if (landmarkId && landmarkName) {
    // For multiple landmark IDs, use the first one for the search item
    // (backward compatibility - searchItem typically represents a single selection)
    const firstLandmarkId = landmarkId.split(',')[0]
    return {
      searchType: 'landmark',
      value: firstLandmarkId,
      format: {
        mainText: landmarkName,
      },
    }
  }

  const bounds = urlSearchParams.get('bounds')

  if (bounds) {
    return {
      searchType: 'bounds',
      value: bounds,
      format: {
        mainText: 'Search area',
      },
    }
  }

  const lat = urlSearchParams.get('lat')
  const lng = urlSearchParams.get('lng')

  if (lat && lng) {
    return {
      searchType: 'lat_long',
      value: `${lat},${lng}`,
      format: {
        mainText: `Location (${lat}, ${lng})`,
      },
    }
  }

  const query = urlSearchParams.get('query')
  const typeaheadId = urlSearchParams.get(queryKeyTypeaheadId)
  if (query && typeaheadId) {
    const extracting = urlSearchParams.get(queryKeyExtracting) === 'true'

    // Build extractedParams from URL when extraction is complete
    let extractedParams: App.ExtractedSearchParamsV2 | undefined
    if (!extracting) {
      const destinationId = urlSearchParams.get(queryKeyDestinationId)
      const landmarkId = urlSearchParams.get(queryKeyLandmarkId)
      const propertyId = urlSearchParams.get(queryKeyPropertyId)
      const lat = urlSearchParams.get(queryKeyLat)
      const lng = urlSearchParams.get(queryKeyLng)
      const checkIn = urlSearchParams.get(queryKeyCheckIn)
      const checkOut = urlSearchParams.get(queryKeyCheckOut)
      const flexibleNights = urlSearchParams.get(queryKeyFlexibleNights)
      const flexibleMonths = urlSearchParams.get(queryKeyFlexibleMonths)
      const amenities = urlSearchParams.get(queryKeyAmenities)
      const inclusions = urlSearchParams.get(queryKeyInclusions)
      const tags = urlSearchParams.get(queryKeyTags)
      const priceGte = urlSearchParams.get(queryKeyPriceGte)
      const priceLte = urlSearchParams.get(queryKeyPriceLte)
      const sortBy = urlSearchParams.get(queryKeySortBy)

      const baseParams: App.ExtractedSearchParamsBase = {
        query,
        ...(checkIn && { checkIn }),
        ...(checkOut && { checkOut }),
        ...(flexibleNights && { flexibleNights }),
        ...(flexibleMonths && { flexibleMonths }),
        ...(amenities && { amenities: amenities.split(',') }),
        ...(inclusions && { inclusions: inclusions.split(',') }),
        ...(tags && { tags: tags.split(',') }),
        ...(priceGte && { priceGte }),
        ...(priceLte && { priceLte }),
        ...(sortBy === 'value' && { sortBy: 'value' as const }),
      }

      if (propertyId) {
        extractedParams = { ...baseParams, propertyId }
      } else if (lat && lng) {
        extractedParams = { ...baseParams, lat, lng, searchType: 'latLong' }
      } else if (landmarkId) {
        extractedParams = { ...baseParams, placeIds: [landmarkId], searchType: 'landmark' }
      } else if (destinationId) {
        extractedParams = { ...baseParams, placeIds: destinationId.split(','), searchType: 'destination' }
      } else {
        extractedParams = { ...baseParams, placeIds: [], searchType: 'destination' }
      }
    }

    return {
      searchType: 'searchItemV3',
      value: query,
      format: {
        mainText: query
      },
      display: {
        mainText: query,
        searchType: 'naturalLanguage',
      },
      searchItem: {
        typeaheadId,
        extractedData: extractedParams ?? {},
        extracting: extracting ?? false,
      },
    }
  }

  return undefined
}

export function getOccupanciesFromURLSearchParams(urlSearchParams: URLSearchParams): Array<App.Occupants> | undefined {
  const roomsAdults: Array<number> = urlSearchParams.get(queryKeyAdults)?.split(',').map(roomAdults => Number(roomAdults)) ?? []

  if (!roomsAdults?.length) return undefined

  const roomsChildrenAges: Array<Array<number>> = urlSearchParams.get(queryKeyChildren)?.split(',')
    .map(roomChildrenAges => roomChildrenAges === 'none' ? [] : roomChildrenAges.split(';').map(childrenAges => Number(childrenAges))) ?? []

  if (roomsChildrenAges?.length !== roomsAdults.length) {
    return roomsAdults.map((adults) => ({ adults }))
  }

  return roomsAdults.map((roomAdults, roomIndex) => {
    const childrenAge = roomsChildrenAges[roomIndex]

    return {
      adults: roomAdults,
      children: childrenAge?.length,
      childrenAge,
    }
  })
}

export function getDestinationIdFromURLSearchParams(urlSearchParams: URLSearchParams): string | undefined {
  return urlSearchParams.get(queryKeyDestinationId) ?? undefined
}

export function getLandmarkIdFromURLSearchParams(urlSearchParams: URLSearchParams): string | undefined {
  return urlSearchParams.get(queryKeyLandmarkId) ?? undefined
}

export function getCheckInDateFromURLSearchParams(urlSearchParams: URLSearchParams): Moment.Moment | undefined {
  const checkIn = urlSearchParams.get(queryKeyCheckIn)

  return checkIn ? Moment(checkIn) : undefined
}

export function getCheckOutDateFromURLSearchParams(urlSearchParams: URLSearchParams): Moment.Moment | undefined {
  const checkOut = urlSearchParams.get(queryKeyCheckOut)

  return checkOut ? Moment(checkOut) : undefined
}

export function getPriceLteFromURLSearchParams(urlSearchParams: URLSearchParams): number | undefined {
  const priceLte = urlSearchParams.get(queryKeyPriceLte)

  return priceLte ? Number(priceLte) : undefined
}

export function getPriceGteFromURLSearchParams(urlSearchParams: URLSearchParams): number | undefined {
  const priceGte = urlSearchParams.get(queryKeyPriceGte)

  return priceGte ? Number(priceGte) : undefined
}

export function getFlexibleNightsFromURLSearchParams(urlSearchParams: URLSearchParams): string | undefined {
  const flexibleNights = urlSearchParams.get(queryKeyFlexibleNights)

  return flexibleNights ?? undefined
}

export function getFlexibleMonthsFromURLSearchParams(urlSearchParams: URLSearchParams): string | undefined {
  const flexibleMonths = urlSearchParams.get(queryKeyFlexibleMonths)

  return flexibleMonths ?? undefined
}

export function getUserSelectedFlexibleMonthsFromURLSearchParams(urlSearchParams: URLSearchParams): boolean {
  const userSelectedFlexibleMonths = urlSearchParams.get(queryKeyUserSelectedFlexibleMonths)

  return userSelectedFlexibleMonths === 'true'
}

export function getDataSearchOptionIdFromURLSearchParams(urlSearchParams: URLSearchParams): DATE_SEARCH_OPTION_IDS {
  const flexibleNights = urlSearchParams.get(queryKeyFlexibleNights)
  const flexibleMonths = urlSearchParams.get(queryKeyFlexibleMonths)
  if (!!flexibleMonths && !!flexibleNights) {
    return DATE_SEARCH_OPTION_IDS.FLEXIBLE
  }
  const checkIn = urlSearchParams.get(queryKeyCheckIn)
  const checkOut = urlSearchParams.get(queryKeyCheckOut)
  if (!!checkIn && !!checkOut) {
    return DATE_SEARCH_OPTION_IDS.SPECIFIC
  }
  return DATE_SEARCH_OPTION_IDS.ANYTIME
}

export function getPageFromURLSearchParams(urlSearchParams: URLSearchParams): number | undefined {
  return Number(urlSearchParams.get(queryKeyPages))
}

export function getLocationsFromURLSearchParams(urlSearchParams: URLSearchParams): Array<string> {
  return urlSearchParams.get(queryKeyLocations)?.split(',') ?? []
}

export function getHolidayTypesFromURLSearchParams(urlSearchParams: URLSearchParams): Array<string> {
  return urlSearchParams.get(queryKeyHolidayTypes)?.split(',') ?? []
}

export function getAmenitiesFromURLSearchParams(urlSearchParams: URLSearchParams): Array<string> {
  return urlSearchParams.get(queryKeyAmenities)?.split(',') ?? []
}

export function getPropertyTypesFromURLSearchParams(urlSearchParams: URLSearchParams): Array<string> {
  return urlSearchParams.get(queryKeyPropertyTypes)?.split(',') ?? []
}

export function getSelectedOfferIdsFromURLSearchParams(urlSearchParams: URLSearchParams): Array<string> {
  return urlSearchParams.get(queryKeySelectedOfferIds)?.split(',') ?? []
}

export function getInclusionsFromURLSearchParams(urlSearchParams: URLSearchParams): Array<string> {
  return urlSearchParams.get(queryKeyInclusions)?.split(',') ?? []
}

export function getMapPinCountFromURLSearchParams(urlSearchParams: URLSearchParams): number | undefined {
  return Number(urlSearchParams.get(queryKeyMapPinCount))
}

export function propertyToDestinationSearch(search: string, destination?: App.Place) {
  if (!destination) return search
  return setManySearchParamValues(deleteSearchParams(search, 'propertyName', 'propertyId'),
    [
      { paramName: 'destinationName', paramValue: destination.name },
      { paramName: 'destinationId', paramValue: destination.id },
    ],
  )
}

export function buildUrl(
  path: string,
  region: string,
  search?: string,
  hash?: string,
) {
  let newPath = addRegionToPath(path, region)

  if (search) {
    newPath += search.startsWith('?') ? search : `?${search}`
  }
  if (hash) {
    newPath += hash.startsWith('#') ? hash : `#${hash}`
  }

  return newPath
}

export function generateCategorySearchUrl({
  parameterName,
  parameterValue,
}: {
  parameterName: string | Array<string> | undefined
  parameterValue: string | Array<string> | undefined
}): string {
  if (parameterName === 'link' && typeof parameterValue === 'string') {
    return parameterValue
  }

  let parameters: Record<string, string> = {}

  if (Array.isArray(parameterValue) && Array.isArray(parameterName)) {
    if (parameterValue.length !== parameterName.length) {
      throw new Error('parameterName and parameterValue arrays must have the same length')
    }

    parameters = parameterName.reduce<Record<string, string>>((acc, name, index) => {
      acc[name] = parameterValue[index]
      return acc
    }, {})
  } else if (typeof parameterValue === 'string' && typeof parameterName === 'string') {
    parameters = { [parameterName]: parameterValue }
  }

  const queryString = qs.stringify({
    destinationId: Places.Anywhere.id,
    destinationName: Places.Anywhere.name,
    ...parameters,
    adults: 2,
    children: 'none',
  }, {
    arrayFormat: 'comma',
  })

  return `/search?${queryString}`
}
